// src/core/assets/icons.ts
// All icon assets — react-icons/lu only.
// Minority icon sets (fi, tb, bs) have been replaced with Lucide equivalents.

import type { IconType } from "react-icons";
import {
  // ── Arrows & navigation ────────────────────────────────────────────────────
  LuArrowUpAZ,
  LuArrowUpZA,
  LuArrowDownAZ,
  LuArrowDownWideNarrow,
  LuArrowLeft,
  LuArrowRight,
  LuArrowUpRight,
  LuArrowLeftToLine,
  LuArrowLeftFromLine,
  LuArrowRightLeft,
  LuChevronDown,
  LuChevronUp,
  LuChevronRight,
  LuChevronsUp,
  LuMoveRight,
  LuMoveUp,
  LuMoveDown,
  LuMove,
  // ── User & people ──────────────────────────────────────────────────────────
  LuUser,
  LuUserCheck,
  LuUserCog,
  LuUserMinus,
  LuUserPen,
  LuUserPlus,
  LuUserRound,
  LuUserRoundCheck,
  LuUserX,
  LuUsers,
  // ── Files & folders ────────────────────────────────────────────────────────
  LuFile,
  LuFilePlus,
  LuFilePen,
  LuFileBox,
  LuFileClock,
  LuFileImage,
  LuFileScan,
  LuFileStack,
  LuFileUp,
  LuFileUser,
  LuFolder,
  LuFolderOpen,
  LuFolderPen,
  LuFolderPlus,
  LuFolderTree,
  // ── Actions ────────────────────────────────────────────────────────────────
  LuArchive,
  LuArchiveRestore,
  LuCheck,
  LuCopy,
  LuDownload,
  LuCloudUpload,
  LuFilter,
  LuHighlighter,
  LuLogOut,
  LuPencil,
  LuRotateCcw,
  LuSave,
  LuSearch,
  LuSend,
  LuShare,
  LuSquarePen,
  LuTrash,
  LuTrash2,
  LuZoomIn,
  LuZoomOut,
  // ── Status & indicators ────────────────────────────────────────────────────
  LuActivity,
  LuBadgeCheck,
  LuBadgePlus,
  LuBell,
  LuCircle,
  LuCircleAlert,
  LuCircleCheck,
  LuCircleCheckBig,
  LuCircleHelp,
  LuCircleMinus,
  LuCircleOff,
  LuCirclePause,
  LuCirclePlay,
  LuCirclePlus,
  LuCircleX,
  LuCloud,
  LuDatabase,
  LuHourglass,
  LuInbox,
  LuPercent,
  LuShield,
  LuShieldCheck,
  LuShieldX,
  LuUnlink,
  LuStar,
  LuTriangleAlert,
  // ── Layout & UI ────────────────────────────────────────────────────────────
  LuLayoutDashboard,
  LuLayoutGrid,
  LuLayers,
  LuLayers2,
  LuList,
  LuListPlus,
  LuListTodo,
  LuMaximize2,
  LuPanelLeftClose,
  LuPanelsTopLeft,
  LuSquare,
  LuSquareActivity,
  LuSquareArrowOutUpRight,
  LuSquareUser,
  // ── Misc & domain ─────────────────────────────────────────────────────────
  LuBookCopy,
  LuBookKey,
  LuBuilding2,
  LuCalendarDays,
  LuClockArrowUp,
  LuClock2,
  LuEllipsisVertical, // replaces FiMoreVertical
  LuEye,
  LuEyeOff,
  LuGlobe,
  LuHand,
  LuHardHat,
  LuHouse,
  // ── CAD viewer ─────────────────────────────────────────────────────────────
  LuBox,
  LuEraser,
  LuRuler,
  LuSnowflake,
  LuSpline,
  LuTriangleRight,
  LuLock,
  LuLockKeyhole,
  LuLockOpen, // replaces BsUnlock
  LuLogs,
  LuMapPin,
  LuMessageCircle,
  LuMessageCircleQuestion,
  LuMinus,
  LuMousePointer2, // replaces BsFillCursorFill
  LuNewspaper,
  LuNotepadText,
  LuPaperclip,
  LuPhone,
  LuPlus, // replaces FiPlus
  LuReceiptText,
  LuSettings,
  LuShapes,
  LuSlidersHorizontal,
  LuTag,
  LuTags,
  LuTrendingUp,
  LuTrendingUpDown,
  LuTriangle,
  LuType,
  LuWallet,
  LuX,
  LuHash,
  LuInfo,
} from "react-icons/lu";

// ─────────────────────────────────────────────────────────────────────────────

export type IconKey =
  // Arrows & navigation
  | "arrow-up-a-z"
  | "arrow-up-z-a"
  | "arrow-down-a-z"
  | "arrow-down-wide-narrow"
  | "arrow-left"
  | "arrow-right"
  | "arrow-up-right"
  | "arrow-left-to-line"
  | "arrow-left-from-line"
  | "arrow-right-left"
  | "chevron-down"
  | "chevron-up"
  | "chevron-right"
  | "chevrons-up"
  | "move-right"
  | "move-up"
  | "move-down"
  | "move"
  // User & people
  | "user"
  | "user-check"
  | "user-cog"
  | "user-minus"
  | "user-pen"
  | "user-plus"
  | "user-star"
  | "user-round-check"
  | "user-x"
  | "users"
  // Files & folders
  | "file"
  | "file-plus"
  | "file-pen"
  | "file-box"
  | "file-clock"
  | "file-image"
  | "file-scan"
  | "file-stack"
  | "file-up"
  | "file-user"
  | "folder"
  | "folder-open"
  | "folder-pen"
  | "folder-plus"
  | "folder-tree"
  // Actions
  | "archive"
  | "archive-restore"
  | "check"
  | "copy"
  | "download"
  | "cloud-upload"
  | "filter"
  | "highlighter"
  | "log-out"
  | "pencil"
  | "rotate-ccw"
  | "save"
  | "search"
  | "send"
  | "share"
  | "square-pen"
  | "trash"
  | "trash-2"
  | "zoom-in"
  | "zoom-out"
  // Status & indicators
  | "activity"
  | "badge-check"
  | "badge-plus"
  | "bell"
  | "circle"
  | "circle-alert"
  | "circle-check"
  | "circle-check-big"
  | "circle-help"
  | "circle-minus"
  | "circle-off"
  | "circle-pause"
  | "circle-play"
  | "circle-plus"
  | "circle-x"
  | "cloud"
  | "database"
  | "hourglass"
  | "inbox"
  | "percent"
  | "shield"
  | "shield-check"
  | "shield-x"
  | "unlink"
  | "star"
  | "triangle-alert"
  // Layout & UI
  | "layout-dashboard"
  | "layout-grid"
  | "layers"
  | "layers-2"
  | "list"
  | "list-plus"
  | "list-todo"
  | "maximize"
  | "panel-left-close"
  | "panels-top-left"
  | "square"
  | "square-activity"
  | "square-arrow-out-up-right"
  | "square-user"
  // Misc & domain
  | "book-copy"
  | "book-key"
  | "building"
  | "calendar-days"
  | "clock"
  | "clock-arrow-up"
  | "cursor"
  | "eye"
  | "eye-off"
  | "globe"
  | "hard-hat"
  | "house"
  | "lock"
  | "lock-keyhole"
  | "unlock"
  | "logs"
  | "map-pin"
  | "message-circle"
  | "message-circle-question"
  | "minus"
  | "more-vertical"
  | "newspaper"
  | "notepad-text"
  | "paperclip"
  | "phone"
  | "plus"
  | "receipt-text"
  | "settings"
  | "shapes"
  | "sliders-horizontal"
  | "tag"
  | "tags"
  | "trending-up"
  | "trending-up-down"
  | "triangle"
  | "type"
  | "wallet"
  | "hash"
  | "x-close"
  | "info"
  // CAD viewer
  | "box"
  | "eraser"
  | "hand"
  | "ruler"
  | "snowflake"
  | "spline"
  | "triangle-right";

export const icons: Record<IconKey, IconType> = {
  // Arrows & navigation
  "arrow-up-a-z": LuArrowUpAZ,
  "arrow-up-z-a": LuArrowUpZA,
  "arrow-down-a-z": LuArrowDownAZ,
  "arrow-down-wide-narrow": LuArrowDownWideNarrow,
  "arrow-left": LuArrowLeft,
  "arrow-right": LuArrowRight,
  "arrow-up-right": LuArrowUpRight,
  "arrow-left-to-line": LuArrowLeftToLine,
  "arrow-left-from-line": LuArrowLeftFromLine,
  "arrow-right-left": LuArrowRightLeft,
  "chevron-down": LuChevronDown,
  "chevron-up": LuChevronUp,
  "chevron-right": LuChevronRight,
  "chevrons-up": LuChevronsUp,
  "move-right": LuMoveRight,
  "move-up": LuMoveUp,
  "move-down": LuMoveDown,
  move: LuMove,
  // User & people
  user: LuUser,
  "user-check": LuUserCheck,
  "user-cog": LuUserCog,
  "user-minus": LuUserMinus,
  "user-pen": LuUserPen,
  "user-plus": LuUserPlus,
  "user-star": LuUserRound, // TbUserStar → LuUserRound (no Lucide equivalent)
  "user-round-check": LuUserRoundCheck,
  "user-x": LuUserX,
  users: LuUsers,
  // Files & folders
  file: LuFile,
  "file-plus": LuFilePlus,
  "file-pen": LuFilePen,
  "file-box": LuFileBox,
  "file-clock": LuFileClock,
  "file-image": LuFileImage,
  "file-scan": LuFileScan,
  "file-stack": LuFileStack,
  "file-up": LuFileUp,
  "file-user": LuFileUser,
  folder: LuFolder,
  "folder-open": LuFolderOpen,
  "folder-pen": LuFolderPen,
  "folder-plus": LuFolderPlus,
  "folder-tree": LuFolderTree,
  // Actions
  archive: LuArchive,
  "archive-restore": LuArchiveRestore,
  check: LuCheck,
  copy: LuCopy,
  download: LuDownload,
  "cloud-upload": LuCloudUpload,
  filter: LuFilter,
  highlighter: LuHighlighter,
  "log-out": LuLogOut,
  pencil: LuPencil,
  "rotate-ccw": LuRotateCcw,
  save: LuSave,
  search: LuSearch,
  send: LuSend,
  share: LuShare,
  "square-pen": LuSquarePen,
  trash: LuTrash,
  "trash-2": LuTrash2,
  "zoom-in": LuZoomIn,
  "zoom-out": LuZoomOut,
  // Status & indicators
  activity: LuActivity,
  "badge-check": LuBadgeCheck,
  "badge-plus": LuBadgePlus,
  bell: LuBell,
  circle: LuCircle,
  "circle-alert": LuCircleAlert,
  "circle-check": LuCircleCheck,
  "circle-check-big": LuCircleCheckBig,
  "circle-help": LuCircleHelp,
  "circle-minus": LuCircleMinus,
  "circle-off": LuCircleOff,
  "circle-pause": LuCirclePause,
  "circle-play": LuCirclePlay,
  "circle-plus": LuCirclePlus,
  "circle-x": LuCircleX,
  cloud: LuCloud,
  database: LuDatabase,
  hourglass: LuHourglass,
  inbox: LuInbox,
  percent: LuPercent,
  shield: LuShield,
  "shield-check": LuShieldCheck,
  "shield-x": LuShieldX,
  unlink: LuUnlink,
  star: LuStar,
  "triangle-alert": LuTriangleAlert,
  // Layout & UI
  "layout-dashboard": LuLayoutDashboard,
  "layout-grid": LuLayoutGrid,
  layers: LuLayers,
  "layers-2": LuLayers2,
  list: LuList,
  "list-plus": LuListPlus,
  "list-todo": LuListTodo,
  maximize: LuMaximize2,
  "panel-left-close": LuPanelLeftClose,
  "panels-top-left": LuPanelsTopLeft,
  square: LuSquare,
  "square-activity": LuSquareActivity,
  "square-arrow-out-up-right": LuSquareArrowOutUpRight,
  "square-user": LuSquareUser,
  // Misc & domain
  "book-copy": LuBookCopy,
  "book-key": LuBookKey,
  building: LuBuilding2,
  "calendar-days": LuCalendarDays,
  clock: LuClock2,
  "clock-arrow-up": LuClockArrowUp,
  cursor: LuMousePointer2, // BsFillCursorFill → LuMousePointer2
  eye: LuEye,
  "eye-off": LuEyeOff,
  globe: LuGlobe,
  "hard-hat": LuHardHat,
  house: LuHouse,
  lock: LuLock,
  "lock-keyhole": LuLockKeyhole,
  unlock: LuLockOpen, // BsUnlock → LuLockOpen
  logs: LuLogs,
  "map-pin": LuMapPin,
  "message-circle": LuMessageCircle,
  "message-circle-question": LuMessageCircleQuestion,
  minus: LuMinus,
  "more-vertical": LuEllipsisVertical, // FiMoreVertical → LuEllipsisVertical
  newspaper: LuNewspaper,
  "notepad-text": LuNotepadText,
  paperclip: LuPaperclip,
  phone: LuPhone,
  plus: LuPlus, // FiPlus → LuPlus
  "receipt-text": LuReceiptText,
  settings: LuSettings,
  shapes: LuShapes,
  "sliders-horizontal": LuSlidersHorizontal,
  tag: LuTag,
  tags: LuTags,
  "trending-up": LuTrendingUp,
  "trending-up-down": LuTrendingUpDown,
  triangle: LuTriangle,
  type: LuType,
  wallet: LuWallet,
  "x-close": LuX,
  hash: LuHash,
  info: LuInfo,
  // CAD viewer
  box: LuBox,
  eraser: LuEraser,
  hand: LuHand,
  ruler: LuRuler,
  snowflake: LuSnowflake,
  spline: LuSpline,
  "triangle-right": LuTriangleRight,
};
